type FetchFunction = (
  input: RequestInfo | URL,
  init?: RequestInit,
) => Promise<Response>;

type UnauthorizedHandler = () => void;

interface RawApiEnvelope {
  code: number;
  msg: string;
  data?: unknown;
  extras?: unknown;
}

export interface ApiResult<TData, TExtras> {
  code: 0;
  msg: string;
  data: TData | undefined;
  extras: TExtras | undefined;
}

export type ApiErrorKind = "business" | "http" | "network" | "protocol";

export class ApiClientError extends Error {
  readonly kind: ApiErrorKind;
  readonly status: number | undefined;
  readonly code: number | undefined;
  readonly data: unknown;

  constructor(
    message: string,
    kind: ApiErrorKind,
    options: {
      status?: number;
      code?: number;
      data?: unknown;
      cause?: unknown;
    } = {},
  ) {
    super(message, { cause: options.cause });
    this.name = "ApiClientError";
    this.kind = kind;
    this.status = options.status;
    this.code = options.code;
    this.data = options.data;
  }
}

export function should_retry_api_query(
  failure_count: number,
  error: unknown,
): boolean {
  if (failure_count >= 1 || !(error instanceof ApiClientError)) {
    return false;
  }
  return (
    error.kind === "network" ||
    (error.kind === "http" &&
      error.status !== undefined &&
      error.status >= 500)
  );
}

function is_record(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function parse_envelope(payload: unknown): RawApiEnvelope {
  if (
    !is_record(payload) ||
    typeof payload.code !== "number" ||
    typeof payload.msg !== "string"
  ) {
    throw new ApiClientError("服务器响应格式不正确", "protocol");
  }

  return {
    code: payload.code,
    msg: payload.msg,
    ...(Object.hasOwn(payload, "data") ? { data: payload.data } : {}),
    ...(Object.hasOwn(payload, "extras") ? { extras: payload.extras } : {}),
  };
}

function default_unauthorized_handler() {
  if (
    typeof window !== "undefined" &&
    window.location.pathname !== "/login"
  ) {
    window.location.assign("/login");
  }
}

export class ApiClient {
  private readonly fetch_function: FetchFunction;
  private readonly unauthorized_handler: UnauthorizedHandler;

  constructor(
    fetch_function: FetchFunction = (input, init) => fetch(input, init),
    unauthorized_handler: UnauthorizedHandler = default_unauthorized_handler,
  ) {
    this.fetch_function = fetch_function;
    this.unauthorized_handler = unauthorized_handler;
  }

  async request<TData, TExtras = Record<string, never>>(
    path: string,
    init: RequestInit = {},
  ): Promise<ApiResult<TData, TExtras>> {
    if (!path.startsWith("/") || path.startsWith("//")) {
      throw new ApiClientError("API 路径必须是站内绝对路径", "protocol");
    }

    const headers = new Headers(init.headers);
    if (init.body !== undefined && !(init.body instanceof FormData)) {
      headers.set("Content-Type", "application/json");
    }

    let response: Response;
    try {
      const request_url = new URL(path, window.location.origin);
      response = await this.fetch_function(request_url, {
        ...init,
        credentials: "include",
        headers,
      });
    } catch (error) {
      if (error instanceof ApiClientError) {
        throw error;
      }
      throw new ApiClientError("网络连接失败，请稍后重试", "network", {
        cause: error,
      });
    }

    if (response.status === 401) {
      this.unauthorized_handler();
      let unauthorized_envelope: RawApiEnvelope | undefined;
      try {
        unauthorized_envelope = parse_envelope(await response.json());
      } catch {
        // Authentication expiry must redirect even when a proxy returns HTML
        // or an empty body instead of the normal JSON envelope.
      }
      throw new ApiClientError(
        unauthorized_envelope?.msg || "登录状态已失效",
        "http",
        {
          status: 401,
          ...(unauthorized_envelope === undefined
            ? {}
            : { code: unauthorized_envelope.code }),
          data: unauthorized_envelope?.data,
        },
      );
    }

    let envelope: RawApiEnvelope;
    try {
      envelope = parse_envelope(await response.json());
    } catch (error) {
      if (error instanceof ApiClientError) {
        throw error;
      }
      throw new ApiClientError("服务器响应无法解析", "protocol", {
        status: response.status,
        cause: error,
      });
    }

    if (envelope.code === 401) {
      this.unauthorized_handler();
      throw new ApiClientError(envelope.msg || "登录状态已失效", "http", {
        status: 401,
        code: envelope.code,
      });
    }

    if (!response.ok) {
      throw new ApiClientError(envelope.msg || "服务器请求失败", "http", {
        status: response.status,
        code: envelope.code,
        data: envelope.data,
      });
    }

    if (envelope.code !== 0) {
      throw new ApiClientError(envelope.msg || "操作失败", "business", {
        status: response.status,
        code: envelope.code,
        data: envelope.data,
      });
    }

    return {
      code: 0,
      msg: envelope.msg,
      data: envelope.data as TData | undefined,
      extras: envelope.extras as TExtras | undefined,
    };
  }

  get<TData, TExtras = Record<string, never>>(
    path: string,
    init: Omit<RequestInit, "method"> = {},
  ) {
    return this.request<TData, TExtras>(path, {
      ...init,
      method: "GET",
    });
  }

  post<TData, TExtras = Record<string, never>>(
    path: string,
    body: unknown,
    init: Omit<RequestInit, "method" | "body"> = {},
  ) {
    return this.request<TData, TExtras>(path, {
      ...init,
      method: "POST",
      body: JSON.stringify(body),
    });
  }
}

export const api_client = new ApiClient();
